import {addNotice, removeNotice} from "./notices";

export default class Signup {
  constructor() {
    this.form = document.getElementById( 'td-signup' );
    this.action = 'check_marketo_user';

    if (!this.form) {
      return;
    }

    this.fetchSignupCount();

    this.emailField = document.getElementById( 'user_email' );
    this.gdprField = document.getElementById( 'gdpr_consent' );
    this.submitButton = this.form.querySelector( '[type="submit"]' );
    this.nonceField = document.getElementById( 'nonce' );

    this.isFormValid = false;
    this.consentIsGiven = false;
    this.isError = false;

    // Timer for the keyup event with delay/debounce.
    this.timer = null;
    this.waitTime = 150;

    this.captcha = window.signup_variables.captcha || {};
    this.turnstileToken = null;

    this.bindEvents();
    this.windowEvents();
    this.keyboardEvents();
    this.renderTurnstile();
  }

  renderTurnstile() {
    if ( this.captcha.type !== 'turnstile' || ! this.captcha.key ) {
      return;
    }

    const container = document.createElement( 'div' );
    this.form.querySelector( '[type="submit"]' ).before( container );

    turnstile.render( container, {
      sitekey: this.captcha.key,
      callback: ( token ) => { this.turnstileToken = token; },
    } );
  }

  /**
   * Bind events to the class.
   */
  bindEvents() {
    this.validateForm = this.validateForm.bind( this );
  }

  windowEvents() {
    window.addEventListener( 'validateForm', this.validateForm );
  }

  /**
   * Detect if the form is valid.
   * Enables or disables the form-ajax request.
   * On a valid form it will remove the error messages.
   */
  validateForm() {
    const isValid = this.consentIsGiven && this.nonceField.value && this.validateEmailField();

    if (isValid) {
      removeNotice( 'signup-notification-consent' );
      removeNotice( 'signup-notification' );
    }

    this.submitButton.disabled = !isValid;

    this.isFormValid = isValid;
  }

  keyboardEvents() {

    // Detect if user has CapsLock enabled.
    document.addEventListener( 'keydown', (event) => {
      if (!!event.getModifierState( 'CapsLock' )) {
        addNotice( window.signup_variables.errors.capslock_on, 'signup-notification', 'alert', this.form );
      } else {
        removeNotice( 'signup-notification' );
      }
    } );

    this.gdprField.addEventListener( 'change', (event) => {
      this.consentIsGiven = event.target.checked;

      window.dispatchEvent( new CustomEvent( "validateForm" ) );

      if (!this.consentIsGiven) {
        addNotice( 'Please accept the terms and conditions to proceed.', this.form, 'signup-notification-consent', 'alert' );
      } else {
        removeNotice( 'signup-notification-consent' );
      }
    } );

    console.info('Signup', window.signup_variables.captcha );

    this.form.addEventListener( 'submit', (event) => {
        event.preventDefault();

        if ( this.captcha.type === 'recaptcha' && this.captcha.key ) {
          grecaptcha.ready( () => grecaptcha.execute( this.captcha.key, {action: 'submit'} ).then( () => this.ajaxCall() ) );
        } else if ( this.captcha.type === 'turnstile' && this.turnstileToken ) {
          this.ajaxCall();
        } else if ( this.captcha.type === '' ) {
          this.ajaxCall();
        }
      }
    );

    this.emailField.addEventListener(
      'blur',
      (e) => {
        e.preventDefault();
        removeNotice( 'signup-user-exists' );
        clearTimeout( this.timer );
        this.timer = setTimeout( () => this.validateEmailField(), this.waitTime / 3 );
        const isError = !this.validateEmailField();
        if (!isError) {
          removeNotice();
        } else {
          addNotice( this.emailField.value ? window.signup_variables.errors.email_required : window.signup_variables.errors.invalid_email, this.form );
        }

        window.dispatchEvent( new CustomEvent( "validateForm" ) );
      }
    );

    this.emailField.addEventListener(
      'keyup',
      (event) => {
        removeNotice( 'signup-user-exists' );
        if (!!event.getModifierState( 'CapsLock' )) {
          addNotice( window.signup_variables.errors.capslock_on, this.form, 'signup-notification', 'alert' );
        } else {
          removeNotice( 'signup-notification' );
        }

        clearTimeout( this.timer );
        this.timer = setTimeout( () => {
          this.validateEmailField();
          window.dispatchEvent( new CustomEvent( "validateForm" ) );
        }, this.waitTime );
      }
    );
  }

  validateEmailField() {
    if (!this.emailField) {
      return false;
    }

    const pattern = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
    if (pattern.test( this.emailField.value )) {
      this.emailField.classList.remove( 'invalid' );
      return true;
    }

    this.emailField.classList.add( 'invalid' );
    return false;
  }

  /**
   * Fire the ajax call.
   *
   * @return {boolean}
   */
  ajaxCall() {
    if (!this.isFormValid) {
      return false;
    }

    this.updateEmailFieldStatus( true );

    fetch(
      window.signup_variables.ajax_url,
      {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        body: new URLSearchParams(
          {
            action: this.action,
            nonce: this.nonceField.value.trim(),
            email: this.emailField.value.trim(),
          }
        ).toString()
      }
    )
      .then( response => response.json() )
      .then(
        response => {
          if (response.success) {
            this.emailField.removeAttribute( 'aria-describedby' );
            removeNotice( 'signup-user-exists' );
            document.getElementById( 'redirect' ).value = response.data.redirect;
            document.getElementById( 'user_id' ).value = response.data.user_id;
            document.getElementById( 'hash' ).value = response.data.hash;
            this.form.action = response.data.redirect;
            this.form.submit();
          } else {
            this.emailField.classList.add( 'invalid' );
            this.submitButton.disabled = true;
            this.emailField.setAttribute( 'aria-describedby', 'signup-user-exists' );
            addNotice( response.data.message, this.form, 'signup-user-exists', 'error' );
            throw new Error( response.data.message );
          }
        }
      )
      .finally( this.updateEmailFieldStatus )
      .catch( this.updateEmailFieldStatus );
  }

  /**
   * Update the e-mail field status.
   *
   * @param {boolean} isDisabled E-mail field status, disabled or enabled.
   */
  updateEmailFieldStatus(isDisabled = false, className = 'loading') {
    if (!this.emailField) {
      return;
    }

    this.emailField.disabled = isDisabled;
    isDisabled ? this.emailField.classList.add( className ) : this.emailField.classList.remove( className );
  }

  /**
   * Fetch hourly signup count.
   * @return {Promise<void>}
   */
  async fetchSignupCount() {
    try {
      const url = new URL( window.signup_variables.ajax_url );
      url.searchParams.append( 'action', 'retrieve_signup_count' );
      url.searchParams.append( 'nonce', window.wp_variables.ajax_nonce );

      const response = await fetch( url );

      if (!response.ok) {
        throw new Error( 'Network response was not ok' );
      }

      this.hourlySignupObject = await response.json();

      const hourlySignupLimit = this.hourlySignupObject.data?.signup_limit || 240;
      if (this.hourlySignupObject.data.signup_count > hourlySignupLimit) {
        addNotice( `<strong>${this.hourlySignupObject.data.error_title}</strong><br />${this.hourlySignupObject.data.error_message}`, this.form, 'signup-limit-reached', 'error' );
        this.updateEmailFieldStatus( true, 'invalid' );
        this.submitButton.disabled = true;
        this.gdprField.disabled = true;
        return false;
      }
    } catch (error) {
      console.error( 'There has been a problem with your fetch operation:', error );
    }
  }
}
